import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import {
  Link,
  Redirect,
  useParams,
} from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import Iframe from "react-iframe";

/** Must match `document_type` in `tm_printable_sample` (getPrintableSample.php). */
const DOCUMENT_TYPE_RANK_LIST = "rank_list";

function apiPhp(file) {
  return `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${file}`;
}

function normalizePrintableDir(dir) {
  if (!dir || !String(dir).length) return "";
  const s = String(dir);
  return s.endsWith("/") ? s : `${s}/`;
}

/** API may send null, "", or the string "null" for schools with no section split. */
function normalizeSectionId(raw) {
  if (raw == null) return "No";
  const s = String(raw).trim();
  if (s === "" || s.toLowerCase() === "null") return "No";
  return s;
}

function formatParticipatingClassLabel(item) {
  const cn = item.class_name != null ? String(item.class_name) : "";
  const sn =
    item.section_name != null ? String(item.section_name).trim() : "";
  if (sn === "" || sn.toLowerCase() === "null") return cn;
  return `${cn}:${sn}`;
}

function buildRankListIframeUrl({
  fileDirectory,
  fileName,
  reportCardId,
  sessionId,
  schoolCode,
  classId,
  sectionId,
  examId,
  sampleId,
  colorOptionId,
  classSectionName,
  sortBy,
}) {
  const sid = normalizeSectionId(sectionId);
  if (!classId || !fileName || !fileDirectory) return "";
  const dir = normalizePrintableDir(fileDirectory);
  const base = `${process.env.REACT_APP_SERVER_NAME}${dir}${fileName}`;
  const params = new URLSearchParams({
    report_card_id: reportCardId,
    class_id: classId,
    section_id: sid,
    class_section_name: classSectionName || "",
    session_id: sessionId,
    school_code: schoolCode || "",
    exam_id: examId || "null",
    sample_id: sampleId || "",
    color_option_id: colorOptionId || "",
    system_code: process.env.REACT_APP_SYSTEM_CODE || "",
    sort_by: sortBy === "roll_no" ? "roll_no" : "rank",
    _: "0",
  });
  return `${base}?${params.toString()}`;
}

function ExamReportCardRankListContent() {
  const { report_card_id: REPORT_CARD_ID, session_id: SESSION_ID } =
    useParams();

  const token = localStorage.getItem("token");
  const schoolCode = SessionManager.getSchoolCode();
  /** True on first paint when we have route params (matches prior loadingSamples initial true). */
  const [loading, setLoading] = useState(
    () => Boolean(REPORT_CARD_ID && SESSION_ID)
  );
  const [mExamParticipatingClass, setMExamParticipatingClass] = useState([]);

  const [printableSamples, setPrintableSamples] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileDirectory, setFileDirectory] = useState("");
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [colorOptionId, setColorOptionId] = useState("");

  const [examId, setExamId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [classSelectedPosition, setClassSelectedPosition] = useState(-1);
  const [iFrameHeight, setIFrameHeight] = useState("1200px");
  const [showIframeLoader, setShowIframeLoader] = useState(false);
  /** `rank` | `roll_no` — passed as `sort_by` to the printable rank list PHP. */
  const [sortBy, setSortBy] = useState("rank");

  const classSectionName = useMemo(() => {
    if (!classId) return "";
    const sid = normalizeSectionId(sectionId);
    const row = mExamParticipatingClass.find(
      (x) =>
        String(x.class_id) === String(classId) &&
        normalizeSectionId(x.section_id) === sid
    );
    return row ? formatParticipatingClassLabel(row) : "";
  }, [classId, sectionId, mExamParticipatingClass]);

  const rankListUrl = useMemo(
    () =>
      buildRankListIframeUrl({
        fileDirectory,
        fileName,
        reportCardId: REPORT_CARD_ID,
        sessionId: SESSION_ID,
        schoolCode,
        classId,
        sectionId,
        examId,
        sampleId: selectedSampleId,
        colorOptionId,
        classSectionName,
        sortBy,
      }),
    [
      fileDirectory,
      fileName,
      REPORT_CARD_ID,
      SESSION_ID,
      classId,
      sectionId,
      examId,
      selectedSampleId,
      colorOptionId,
      classSectionName,
      sortBy,
    ]
  );

  useEffect(() => {
    const onMessage = (event) => {
      const d = event.data;
      if (!d) return;
      if (d.iframeHeight) {
        setIFrameHeight(`${d.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage, false);
  }, []);

  const clearPrintableTemplate = useCallback(() => {
    setPrintableSamples([]);
    setFileName("");
    setFileDirectory("");
    setSelectedSampleId("");
    setColorOptionId("");
  }, []);

  const setClassList = useCallback(
    (selected_position, participatingClasses) => {
      if (selected_position < 0) return;

      const list =
        participatingClasses?.length
          ? participatingClasses
          : mExamParticipatingClass;

      if (!list.length) return;

      const row = list[selected_position];
      const nextClassId = row.class_id;
      const nextSectionId = normalizeSectionId(row.section_id);

      if (row.exam_id != null && row.exam_id !== "") {
        setExamId(String(row.exam_id));
      }
      setClassId(nextClassId);
      setSectionId(nextSectionId);
      setClassSelectedPosition(selected_position);
    },
    [mExamParticipatingClass]
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    if (!REPORT_CARD_ID || !SESSION_ID) {
      setLoading(false);
      return undefined;
    }
    if (!schoolCode) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    window.scrollTo(0, 0);

    setLoading(true);
    const SCHOOL_CODE = schoolCode;

    const participatingReq = axios
      .get(apiPhp("getReportCardParticipatingClass.php"), {
        params: {
          school_code: SCHOOL_CODE,
          session_id: SESSION_ID,
          report_card_id: REPORT_CARD_ID,
        },
      })
      .then((res) => res.data);

    const printableReq = axios
      .get(apiPhp("getPrintableSample.php"), {
        params: {
          school_code: SCHOOL_CODE,
          document_type: DOCUMENT_TYPE_RANK_LIST,
        },
      })
      .then((res) => res.data);

    Promise.allSettled([participatingReq, printableReq]).then((results) => {
      if (cancelled) return;
      setLoading(false);

      const [participatingResult, printableResult] = results;

      if (participatingResult.status === "fulfilled") {
        const response = participatingResult.value;
        if (response.api_status === "OK") {
          const participating = response.data || [];
          setMExamParticipatingClass(participating);
          if (participating.length > 0) {
            const row = participating[0];
            setExamId(row.exam_id != null ? String(row.exam_id) : "");
            setClassId(row.class_id);
            setSectionId(normalizeSectionId(row.section_id));
            setClassSelectedPosition(0);
          } else {
            setExamId("");
            setClassId("");
            setSectionId("");
            setClassSelectedPosition(-1);
          }
        } else if (response.api_remark) {
          myToast.notify_warning(response.api_remark);
        }
      } else {
        myToast.notify_error("Failed to load participating classes");
      }

      if (printableResult.status === "fulfilled") {
        const response = printableResult.value;
        if (
          response.api_status === "OK" &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          const rows = response.data;
          setPrintableSamples(rows);
          const sample = rows[0];
          setFileName(sample.file_name || "");
          setFileDirectory(sample.file_directory || "");
          setSelectedSampleId(sample.sample_id || "");
          const colors = sample.color_options || [];
          setColorOptionId(colors[0]?.color_option_id || "");
        } else {
          clearPrintableTemplate();
          myToast.notify_warning(
            response.api_remark ||
              "No rank list printable template found (document_type: rank_list)."
          );
        }
      } else {
        myToast.notify_error("Error loading printable samples");
        clearPrintableTemplate();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [REPORT_CARD_ID, SESSION_ID, token, schoolCode, clearPrintableTemplate]);

  useEffect(() => {
    if (rankListUrl) {
      setShowIframeLoader(true);
    }
  }, [rankListUrl]);

  if (!token) {
    return <Redirect to="/Login" />;
  }
  if (loading) {
    return <MyLoader />;
  }

  const hasTemplate = printableSamples.length > 0;
  const showIframe =
    hasTemplate && classId && sectionId && Boolean(rankListUrl);

  return (
    <div className="content">
      <div className="content-header">
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#">Rank List</a>
            </li>
          </ul>
        </div>
      </div>

      <div
        className="animated fadeInUpn bg_page_white_rounded box_shadow"
        style={{ "--mMargin": "50px" }}
      >
        <div
          className="row gravity_center"
          style={{ margin: "2px 15px 2px 15px" }}
        >
          <div
            className="col-sm-6 col-md-6 grid_primery_outline_primery"
            style={{ height: "40px" }}
          >
            <p style={{ padding: "2px", fontSize: "18px" }}>
              <strong>Report card — class rank list</strong>
            </p>
          </div>
          <div
            className="col-sm-3 col-md-3 bg_grid_primary_2"
            style={{ height: "40px" }}
          >
            <div className="gravity_center">
              <Link to="/AdminMain/ExamStudentReportCard/" />
            </div>
          </div>
          <div
            className="col-sm-3 col-md-3 bg_grid_primary_2 "
            style={{ height: "40px" }}
          >
            <div className="gravity_center">
              <Link
                to={`/AdminMain/ExamStudentReportCard/${REPORT_CARD_ID}/${examId}/${SESSION_ID}/null`}
              >
                <button type="button" className="bg_card_primary btn btn-success">
                  Get Report Card
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div style={{ padding: "0px 15px 0px 15px" }}>
          <h5>Select Participating Class:</h5>
        </div>
        <div
          className="row bg_card_primary"
          style={{ padding: "5px", margin: "0px 15px 0px 15px" }}
        >
          {mExamParticipatingClass.map((item, index) => (
            <div
              key={`${item.class_id}-${item.section_id}-${index}`}
              className="col-sm-1 col-md-1"
              style={{ padding: "0px 2px 5px 2px" }}
            >
              <div
                className={
                  classSelectedPosition === index
                    ? "bg_class_select gravity_center"
                    : "bg_class_deselect gravity_center"
                }
                onClick={() => setClassList(index, mExamParticipatingClass)}
                style={{ cursor: "pointer" }}
              >
                <p style={{ fontSize: 14 }}>
                  {formatParticipatingClassLabel(item)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "8px 15px 0" }}>
          <label htmlFor="rankListSortBy" style={{ marginRight: "8px" }}>
            Sort by:
          </label>
          <select
            id="rankListSortBy"
            className="form-control"
            style={{
              display: "inline-block",
              width: "auto",
              minWidth: "160px",
              verticalAlign: "middle",
            }}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="rank">Rank</option>
            <option value="roll_no">Roll No</option>
            
          </select>
        </div>

        {showIframe ? (
          <div
            className="row preview-card"
            style={{
              margin: "0 10px 16px",
              position: "relative",
              minHeight: "200px",
            }}
          >
            {showIframeLoader ? (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "200px",
                  background: "rgba(255,255,255,0.9)",
                }}
                aria-busy="true"
              >
                <MyLoader />
              </div>
            ) : null}
            <div style={{ width: "100%" }}>
              <Iframe
                url={rankListUrl}
                width="100%"
                height={iFrameHeight}
                overflow="visible"
                id="iFrameReportCardRankList"
                frameBorder="0"
                display="initial"
                position="relative"
                onLoad={() => setShowIframeLoader(false)}
              />
            </div>
          </div>
        ) : !hasTemplate ? (
          <div style={{ padding: "12px 15px", color: "#64748b" }}>
            Configure a printable sample with document type &quot;rank_list&quot; to
            show the rank list here.
          </div>
        ) : (
          <div style={{ padding: "12px 15px", color: "#64748b" }}>
            Select a class above to load the rank list.
          </div>
        )}
      </div>
    </div>
  );
}

export default function ExamReportCardRankList() {
  return <ExamReportCardRankListContent />;
}
